import { useEffect, useRef, useState } from 'react';
import type { SettingSchema } from '../../api/types';
import { useEditor } from '../../state/store';
import { ProductPickerModal, CollectionPickerModal } from '../pickers/PickerModal';
import {
  fetchPickerProducts,
  fetchPickerCollections,
  type PickerProduct,
  type PickerCollection,
} from '../../api/client';

const ENGINE_BASE: string =
  (import.meta as any).env?.VITE_ENGINE_URL || '/engine';

/**
 * Polaris-style form fields for the Inspector.
 * Label on top, 13px input, 8px border radius.
 */

export interface FieldProps {
  schema: SettingSchema;
  value: any;
  onChange: (next: any) => void;
}

function Label({ schema }: { schema: SettingSchema }) {
  return (
    <div className="mb-1 flex items-center justify-between">
      <label className="text-[12px] font-medium text-text">
        {schema.label || schema.id}
      </label>
      {schema.info && (
        <span title={schema.info} className="cursor-help text-icon-subdued">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
          </svg>
        </span>
      )}
    </div>
  );
}

function Help({ schema }: { schema: SettingSchema }) {
  if (!schema.info) return null;
  return <p className="mt-1 text-[12px] text-text-subdued">{schema.info}</p>;
}

// ---------------------------------------------------------------------------

export function TextField({ schema, value, onChange }: FieldProps) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => setLocal(value ?? ''), [value]);
  return (
    <div className="mb-4">
      <Label schema={schema} />
      <input
        className="lc-input"
        value={local}
        onChange={(e) => {
          setLocal(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={schema.default ?? ''}
      />
    </div>
  );
}

export function TextareaField({ schema, value, onChange }: FieldProps) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => setLocal(value ?? ''), [value]);
  return (
    <div className="mb-4">
      <Label schema={schema} />
      <textarea
        className="lc-input min-h-[72px] resize-y"
        value={local}
        onChange={(e) => {
          setLocal(e.target.value);
          onChange(e.target.value);
        }}
      />
    </div>
  );
}

/**
 * Rich-text editor backed by contentEditable + the tiny subset of
 * document.execCommand that every browser still supports (bold,
 * italic, underline, ordered/unordered list). execCommand is "soft
 * deprecated" but hasn't actually been removed and ships in every
 * evergreen browser — for a 3-button toolbar this is still the
 * smallest, fastest, most reliable path. No lib, no bundle bloat.
 *
 * Storage format: HTML string. Handled the same way as the previous
 * plain textarea (themes already expect HTML-ish content in these
 * fields), so no data-shape migration is needed.
 */
export function RichTextField({ schema, value, onChange }: FieldProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastPushRef = useRef<string>('');

  // Push a new value from the parent into the editor only if it
  // actually differs from the DOM — otherwise every keystroke would
  // blow away the caret position via a round-trip.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = value ?? '';
    if (next === lastPushRef.current) return;
    if (el.innerHTML !== next) el.innerHTML = next;
    lastPushRef.current = next;
  }, [value]);

  function exec(cmd: string, arg?: string) {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    try {
      // execCommand mutates the DOM inside our contentEditable.
      document.execCommand(cmd, false, arg);
    } catch {}
    flush();
  }

  function flush() {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    lastPushRef.current = html;
    onChange(html);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;
    const k = e.key.toLowerCase();
    if (k === 'b') { e.preventDefault(); exec('bold'); }
    else if (k === 'i') { e.preventDefault(); exec('italic'); }
    else if (k === 'u') { e.preventDefault(); exec('underline'); }
  }

  function onPaste(e: React.ClipboardEvent<HTMLDivElement>) {
    // Strip formatting from pasted text — avoids dumping 50 lines of
    // Microsoft-Word spans into the theme. If the user WANTS the
    // markup they can switch to raw HTML editing in code mode.
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    e.preventDefault();
    document.execCommand('insertText', false, text);
    flush();
  }

  return (
    <div className="mb-4">
      <Label schema={schema} />
      <div className="overflow-hidden rounded border border-border bg-surface focus-within:border-interactive focus-within:ring-2 focus-within:ring-interactive/20">
        <div className="flex items-center gap-0.5 border-b border-border-subdued bg-surface-subdued px-1.5 py-1">
          <ToolbarBtn title="Negrito (Ctrl+B)" onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}>
            <span className="font-bold">B</span>
          </ToolbarBtn>
          <ToolbarBtn title="Itálico (Ctrl+I)" onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}>
            <span className="italic">I</span>
          </ToolbarBtn>
          <ToolbarBtn title="Sublinhado (Ctrl+U)" onMouseDown={(e) => { e.preventDefault(); exec('underline'); }}>
            <span className="underline">U</span>
          </ToolbarBtn>
          <span className="mx-1 h-4 w-px bg-border" />
          <ToolbarBtn title="Lista com marcadores" onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="9" y1="6" x2="20" y2="6" />
              <line x1="9" y1="12" x2="20" y2="12" />
              <line x1="9" y1="18" x2="20" y2="18" />
              <circle cx="4" cy="6" r="1" fill="currentColor" />
              <circle cx="4" cy="12" r="1" fill="currentColor" />
              <circle cx="4" cy="18" r="1" fill="currentColor" />
            </svg>
          </ToolbarBtn>
          <ToolbarBtn title="Lista numerada" onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="10" y1="6" x2="21" y2="6" />
              <line x1="10" y1="12" x2="21" y2="12" />
              <line x1="10" y1="18" x2="21" y2="18" />
              <path d="M4 6h1v4" /><path d="M4 10h2" />
              <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1.5" />
            </svg>
          </ToolbarBtn>
          <span className="mx-1 h-4 w-px bg-border" />
          <ToolbarBtn title="Limpar formatação" onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 7V4h16v3" />
              <path d="M5 20h6" />
              <path d="M13 4 8 20" />
              <line x1="15" y1="15" x2="22" y2="22" />
              <line x1="22" y1="15" x2="15" y2="22" />
            </svg>
          </ToolbarBtn>
        </div>
        <div
          ref={editorRef}
          className="min-h-[80px] px-3 py-2 text-[13px] leading-relaxed text-text focus:outline-none [&_ol]:ml-5 [&_ol]:list-decimal [&_ul]:ml-5 [&_ul]:list-disc"
          contentEditable
          suppressContentEditableWarning
          onInput={flush}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      </div>
      <Help schema={schema} />
    </div>
  );
}

function ToolbarBtn({
  children,
  title,
  onMouseDown,
}: {
  children: React.ReactNode;
  title: string;
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={onMouseDown}
      className="flex h-6 w-6 items-center justify-center rounded text-text hover:bg-surface-hovered"
    >
      {children}
    </button>
  );
}

export function NumberField({ schema, value, onChange }: FieldProps) {
  return (
    <div className="mb-4">
      <Label schema={schema} />
      <input
        type="number"
        className="lc-input"
        value={value ?? ''}
        min={schema.min}
        max={schema.max}
        step={schema.step ?? 1}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

export function RangeField({ schema, value, onChange }: FieldProps) {
  const n = typeof value === 'number' ? value : Number(schema.default ?? schema.min ?? 0);
  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center justify-between">
        <label className="text-[12px] font-medium text-text">{schema.label || schema.id}</label>
        <div className="flex items-center gap-1">
          <input
            type="number"
            className="h-6 w-14 rounded border border-border bg-surface px-1.5 text-right text-[12px]"
            min={schema.min}
            max={schema.max}
            step={schema.step ?? 1}
            value={n}
            onChange={(e) => onChange(Number(e.target.value))}
          />
          {schema.unit && <span className="text-[12px] text-text-subdued">{schema.unit}</span>}
        </div>
      </div>
      <input
        type="range"
        min={schema.min ?? 0}
        max={schema.max ?? 100}
        step={schema.step ?? 1}
        value={n}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

export function CheckboxField({ schema, value, onChange }: FieldProps) {
  return (
    <label className="mb-3 flex cursor-pointer items-center gap-2 text-[13px] text-text">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{schema.label || schema.id}</span>
    </label>
  );
}

export function SelectField({ schema, value, onChange }: FieldProps) {
  return (
    <div className="mb-4">
      <Label schema={schema} />
      <div className="relative">
        <select
          className="lc-input appearance-none pr-8"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        >
          {(schema.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <svg
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-icon-subdued"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
    </div>
  );
}

export function RadioField({ schema, value, onChange }: FieldProps) {
  return (
    <div className="mb-4">
      <Label schema={schema} />
      <div className="grid grid-cols-2 gap-1.5">
        {(schema.options || []).map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              className={
                'rounded-md border px-2.5 py-1.5 text-[12px] transition-all ' +
                (active
                  ? 'border-primary bg-primary text-text-inverse'
                  : 'border-border bg-surface text-text hover:border-border-strong')
              }
              onClick={() => onChange(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ColorField({ schema, value, onChange }: FieldProps) {
  const ref = useRef<HTMLInputElement>(null);
  const hex = toHex(value);
  return (
    <div className="mb-4">
      <Label schema={schema} />
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="relative h-9 w-9 shrink-0 overflow-hidden rounded-md border border-border"
          /*
            Usa LONGHANDS (`backgroundColor` + `backgroundImage`) em vez
            do shorthand `background`. Antes misturava os dois — toda
            mudança no color picker disparava warning do React:
            "Updating a style property (background) when a conflicting
            property is set (backgroundImage) can lead to styling bugs".

            Mecânica: shorthand `background` reseta TODAS as propriedades
            de fundo, inclusive `background-image`. Misturar com
            `backgroundImage` deixa a ordem de aplicação ambígua.
            Longhands resolvem cada propriedade individualmente.

            Comportamento:
              - hex preenchido → `backgroundColor: hex`,
                `backgroundImage: 'none'` (limpa o checker pattern)
              - hex vazio → `backgroundColor: 'transparent'`,
                `backgroundImage: <conic-gradient>` (pattern xadrez)
          */
          style={{
            backgroundColor: hex || 'transparent',
            backgroundImage: hex
              ? 'none'
              : 'conic-gradient(from 180deg at 50% 50%, #eee 0%, #ddd 25%, #eee 50%, #ddd 75%, #eee 100%)',
          }}
          onClick={() => ref.current?.click()}
          title="Escolher cor"
        >
          <input
            ref={ref}
            type="color"
            className="absolute inset-0 h-full w-full opacity-0"
            value={hex || '#000000'}
            onChange={(e) => onChange(e.target.value)}
          />
        </button>
        <input
          className="lc-input font-mono"
          value={value ?? ''}
          placeholder="#000000"
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

export function ImageField({ schema, value, onChange, onAutoCover }: FieldProps & {
  /**
   * Callback OPCIONAL pra que o caller (Inspector) seja notificado
   * quando o user upload um MP4 e o engine extraiu o primeiro frame
   * como capa. Recebe o nome do asset cover (ex.: "abc_video.cover.jpg")
   * pra setar num setting irmão (`cover_image`) do mesmo block/section.
   * Se não fornecido, o cover é ignorado e o user precisa subir capa
   * manualmente.
   */
  onAutoCover?: (coverName: string) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const session = useEditor((s) => s.session);
  // O "image" picker agora aceita mídia geral (imagem + vídeo). Se o
  // valor atual termina em extensão de vídeo, mostra preview do
  // vídeo em vez de quebrar com um <img> sem src.
  const isVideo = typeof value === 'string' &&
    /\.(mp4|webm|mov|ogg|m4v)$/i.test(value);
  // Se o SCHEMA aponta pra vídeo (type=video, id=video ou id termina em
  // _video), abre o picker filtrado só pra vídeo — assim covers .jpg
  // extraídas ficam ocultas e o merchant não seleciona a capa por
  // engano achando que é o vídeo (bug reportado no social-media stories).
  const wantsVideo =
    schema?.type === 'video' ||
    schema?.id === 'video' ||
    (typeof schema?.id === 'string' && /_video$|(^|_)video_/i.test(schema.id));
  const pickerKind: 'image' | 'video' = wantsVideo ? 'video' : 'image';
  const emptyLabel = wantsVideo ? 'Selecionar vídeo' : 'Selecionar mídia';
  return (
    <div className="mb-4">
      <Label schema={schema} />
      {value ? (
        <div className="flex items-center gap-3 rounded-md border border-border bg-surface p-2">
          {isVideo ? (
            <video
              src={resolveAsset(value, session)}
              className="h-14 w-14 rounded border border-border-subdued object-cover"
              muted
              preload="metadata"
              playsInline
            />
          ) : (
            <img
              src={resolveAsset(value, session)}
              alt=""
              className="h-14 w-14 rounded border border-border-subdued object-cover"
              onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12px] text-text">{value}</div>
            <div className="mt-1.5 flex gap-1.5">
              <button
                className="lc-btn-secondary"
                style={{ padding: '2px 8px', fontSize: 12 }}
                onClick={() => setPickerOpen(true)}
              >
                Trocar
              </button>
              <button
                className="lc-btn-ghost"
                style={{ padding: '2px 8px', fontSize: 12, color: '#d72c0d' }}
                onClick={() => onChange('')}
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          className="flex w-full flex-col items-center gap-1 rounded-md border-2 border-dashed border-border bg-surface-subdued py-6 text-[12px] text-text-subdued hover:border-interactive hover:text-text"
          onClick={() => setPickerOpen(true)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-5-5L5 21" />
          </svg>
          {emptyLabel}
        </button>
      )}
      {pickerOpen && (
        <AssetBrowserLazy
          current={value}
          kind={pickerKind}
          onPick={(name, coverName) => {
            onChange(name);
            // Se o upload foi MP4, o engine extraiu o primeiro frame
            // como cover. Repassa pro caller (Inspector) auto-popular
            // a setting `cover_image` irmã se houver.
            if (coverName && onAutoCover) onAutoCover(coverName);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Lazy-imported so the main bundle doesn't pay for the asset browser
 * unless the user actually opens an image picker.
 */
function AssetBrowserLazy(props: {
  current?: string;
  kind?: 'image' | 'video' | 'all';
  onPick: (n: string, coverName?: string) => void;
  onClose: () => void;
}) {
  const [Comp, setComp] = useState<null | React.ComponentType<any>>(null);
  useEffect(() => {
    let cancelled = false;
    import('../AssetBrowser').then((m) => {
      if (!cancelled) setComp(() => m.AssetBrowser);
    });
    return () => { cancelled = true; };
  }, []);
  if (!Comp) return null;
  return <Comp {...props} />;
}

export function UrlField({ schema, value, onChange }: FieldProps) {
  return (
    <div className="mb-4">
      <Label schema={schema} />
      <input
        className="lc-input"
        value={value ?? ''}
        placeholder="/collections/all"
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data-bound selects (menus / products / collections / pages)
// ---------------------------------------------------------------------------

function DataSelect({
  schema,
  value,
  onChange,
  options,
  emptyLabel,
  emptyHint,
}: FieldProps & {
  options: Array<{ value: string; label: string; hint?: string }>;
  emptyLabel: string;
  /**
   * Message shown below the select when `options` is empty. Use this
   * to guide the user to the right place to register data (eg. "crie
   * uma coleção em Painel → Coleções"). Falls back to a generic
   * "Nenhum item disponível" when omitted.
   */
  emptyHint?: string;
}) {
  return (
    <div className="mb-4">
      <Label schema={schema} />
      <div className="relative">
        <select
          className="lc-input appearance-none pr-8"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— {emptyLabel} —</option>
          {options.map((opt) => {
            // Avoid showing "(hint)" when the label already IS the hint
            // (e.g. raw UUIDs) — just render the label.
            const showHint = opt.hint && opt.hint.trim() && opt.hint !== opt.label;
            return (
              <option key={opt.value} value={opt.value}>
                {opt.label}
                {showHint ? ` — ${opt.hint}` : ''}
              </option>
            );
          })}
        </select>
        <svg
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-icon-subdued"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
      {options.length === 0 && (
        <p className="mt-1 text-[11px] text-text-subdued">
          {emptyHint || 'Nenhum item disponível.'}
        </p>
      )}
    </div>
  );
}

/** Shorten a UUID/long key for display. Anything 24+ chars gets truncated. */
function shortKey(key: string): string {
  if (key.length <= 20) return key;
  return `${key.slice(0, 4)}…${key.slice(-4)}`;
}

/**
 * Build a human label for a menu entry. The engine serialises menus as
 * either `{ title, items }` or — more commonly for this stack — the items
 * array directly (so Liquid's `for item in menus[key]` works). In the
 * array case we synthesize a preview from the first 2-3 item titles.
 */
function labelForMenu(key: string, value: any): { label: string; hint?: string } {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const name = value.title || value.name || value.handle;
    if (name) return { label: name, hint: shortKey(key) };
  }
  if (Array.isArray(value) && value.length > 0) {
    const titles = value
      .slice(0, 3)
      .map((it: any) => it?.title || it?.label || it?.name)
      .filter(Boolean);
    if (titles.length) {
      const more = value.length > titles.length ? ` +${value.length - titles.length}` : '';
      return {
        label: `${titles.join(' · ')}${more}`,
        hint: shortKey(key),
      };
    }
  }
  // Nothing better than the id — show it shortened, no duplicate hint.
  return { label: shortKey(key) };
}

export function LinkListField(props: FieldProps) {
  const menus = useEditor((s) => s.metadata?.data.menus || {});
  const options = Object.entries(menus).map(([key, menu]: [string, any]) => ({
    value: key,
    ...labelForMenu(key, menu),
  }));
  return (
    <DataSelect
      {...props}
      options={options}
      emptyLabel="Selecione um menu"
      emptyHint="Nenhum menu cadastrado. Crie um menu no painel da loja e ele aparecerá aqui."
    />
  );
}

/**
 * ProductField — picker de produto com MODAL (search + paginação).
 *
 * Antes era um <select> nativo que mostrava só os 20 produtos que vinham
 * no `metadata.data.products` (limit hard-coded no editor.ts). Loja com
 * 500 produtos = lojista escolhendo entre 20 invisíveis.
 *
 * Bug reportado: "ao escolher um produto em qualquer seção que seja pra
 * escolher um produto, nao aparece todos da loja".
 *
 * Solução: card com o produto selecionado (thumbnail + nome + preço) +
 * botão "Trocar" que abre `<ProductPickerModal>` (search no paycentral +
 * paginação real).
 *
 * Estado vazio: botão "Selecionar produto" abre o modal direto.
 *
 * Compat: aceita valor salvo como uuid/id/handle (legado) — resolve com
 * fuzzy match no metadata pra mostrar nome. Se o produto não estiver no
 * metadata (paginação não traz tudo), faz fetch direto via picker API
 * pra exibir.
 */
export function ProductField(props: FieldProps) {
  const session = useEditor((s) => s.session);
  const products = useEditor((s) => s.metadata?.data.products || []);
  const [open, setOpen] = useState(false);
  const [resolved, setResolved] = useState<PickerProduct | null>(null);

  const rawValue = (props as any).value;
  const valueStr = rawValue ? String(rawValue) : '';

  /*
    Acha o produto pelo valor salvo:
      1. Metadata local (rápido — sem fetch). Match por uuid/id/handle pra
         compat com settings antigos que salvavam id numérico ou handle.
      2. Se não achou e tem valueStr (parece um uuid), busca via picker
         API. Cobre o caso "metadata só tem 20, mas o produto salvo está
         na página 5".
  */
  const metadataMatch = products.find(
    (p: any) =>
      String(p.uuid ?? '') === valueStr ||
      String(p.id ?? '') === valueStr ||
      String(p.handle ?? '') === valueStr
  );

  useEffect(() => {
    if (!valueStr) {
      setResolved(null);
      return;
    }
    if (metadataMatch) {
      setResolved({
        uuid: String(metadataMatch.uuid || valueStr),
        name: metadataMatch.title || metadataMatch.name || '(sem título)',
        image: metadataMatch.image || '',
        amount: typeof metadataMatch.amount === 'number'
          ? metadataMatch.amount
          : parseFloat(metadataMatch.amount) || 0,
        sku: metadataMatch.sku || '',
        active: metadataMatch.active !== false,
      });
      return;
    }
    // Fetch fallback — produto salvo fora dos 20 do metadata
    if (!session) return;
    let cancelled = false;
    fetchPickerProducts(session, { q: valueStr, page: 1 })
      .then((r) => {
        if (cancelled) return;
        const hit = (r.products || []).find((p) => p.uuid === valueStr);
        if (hit) setResolved(hit);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [valueStr, metadataMatch, session]);

  return (
    <div className="mb-4">
      <Label schema={props.schema} />
      {resolved ? (
        <SelectedProductCard
          product={resolved}
          onChange={() => setOpen(true)}
          onClear={() => {
            props.onChange('');
            setResolved(null);
          }}
        />
      ) : valueStr ? (
        // Tem valor salvo mas ainda não resolveu — mostra placeholder do uuid
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-between rounded-md border border-border bg-white px-3 py-2 text-sm hover:border-primary"
        >
          <span className="truncate text-text-subdued">
            Carregando produto <code className="text-xs">{valueStr.slice(0, 8)}…</code>
          </span>
          <span className="text-xs text-primary">Trocar</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border bg-white px-3 py-2.5 text-sm text-text-subdued hover:border-primary hover:bg-primary/5 hover:text-primary"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          Selecionar produto
        </button>
      )}
      <Help schema={props.schema} />
      {open && (
        <ProductPickerModal
          value={valueStr}
          onChange={(uuid) => props.onChange(uuid)}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function SelectedProductCard({
  product,
  onChange,
  onClear,
}: {
  product: PickerProduct;
  onChange: () => void;
  onClear: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-white p-2">
      <div className="h-10 w-10 shrink-0 overflow-hidden rounded border border-border bg-surface">
        {product.image ? (
          <img
            src={product.image}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-icon-subdued">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="9" cy="9" r="2" />
              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
            </svg>
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-text">{product.name}</div>
        <div className="text-xs text-text-subdued">
          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(product.amount)}
        </div>
      </div>
      <button
        type="button"
        onClick={onChange}
        className="rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
      >
        Trocar
      </button>
      <button
        type="button"
        onClick={onClear}
        aria-label="Limpar seleção"
        className="rounded-md p-1 text-text-subdued hover:bg-surface hover:text-red-600"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/**
 * CollectionField — mesmo padrão do ProductField (modal com paginação).
 *
 * Paycentral não suporta `?q=` em /collections, então o filtro dentro do
 * modal é client-side sobre a página atual. Lista de coleções costuma
 * ser pequena (10-50) então não é problema real.
 */
export function CollectionField(props: FieldProps) {
  const session = useEditor((s) => s.session);
  const collections = useEditor((s) => s.metadata?.data.collections || []);
  const [open, setOpen] = useState(false);
  const [resolved, setResolved] = useState<PickerCollection | null>(null);

  const rawValue = (props as any).value;
  const valueStr = rawValue ? String(rawValue) : '';

  const metadataMatch = collections.find(
    (c: any) =>
      String(c.uuid ?? '') === valueStr ||
      String(c.id ?? '') === valueStr ||
      String(c.handle ?? '') === valueStr ||
      String(c.seo_link ?? '') === valueStr
  );

  useEffect(() => {
    if (!valueStr) {
      setResolved(null);
      return;
    }
    if (metadataMatch) {
      setResolved({
        uuid: String(metadataMatch.uuid || valueStr),
        handle: metadataMatch.handle || metadataMatch.seo_link || '',
        title: metadataMatch.title || metadataMatch.name || '(sem título)',
        image: metadataMatch.image || metadataMatch.banner || '',
        description: metadataMatch.description || '',
      });
      return;
    }
    // Fetch fallback — varre página 1 procurando pelo uuid
    if (!session) return;
    let cancelled = false;
    fetchPickerCollections(session, { page: 1 })
      .then((r) => {
        if (cancelled) return;
        const hit = (r.collections || []).find((c) => c.uuid === valueStr);
        if (hit) setResolved(hit);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [valueStr, metadataMatch, session]);

  return (
    <div className="mb-4">
      <Label schema={props.schema} />
      {resolved ? (
        <SelectedCollectionCard
          collection={resolved}
          onChange={() => setOpen(true)}
          onClear={() => {
            props.onChange('');
            setResolved(null);
          }}
        />
      ) : valueStr ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-between rounded-md border border-border bg-white px-3 py-2 text-sm hover:border-primary"
        >
          <span className="truncate text-text-subdued">
            Carregando coleção <code className="text-xs">{valueStr.slice(0, 8)}…</code>
          </span>
          <span className="text-xs text-primary">Trocar</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border bg-white px-3 py-2.5 text-sm text-text-subdued hover:border-primary hover:bg-primary/5 hover:text-primary"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
          Selecionar coleção
        </button>
      )}
      <Help schema={props.schema} />
      {open && (
        <CollectionPickerModal
          value={valueStr}
          onChange={(uuid) => props.onChange(uuid)}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function SelectedCollectionCard({
  collection,
  onChange,
  onClear,
}: {
  collection: PickerCollection;
  onChange: () => void;
  onClear: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-white p-2">
      <div className="h-10 w-10 shrink-0 overflow-hidden rounded border border-border bg-surface">
        {collection.image ? (
          <img
            src={collection.image}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-icon-subdued">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-text">{collection.title}</div>
        {collection.handle && (
          <div className="truncate text-xs text-text-subdued">/{collection.handle}</div>
        )}
      </div>
      <button
        type="button"
        onClick={onChange}
        className="rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
      >
        Trocar
      </button>
      <button
        type="button"
        onClick={onClear}
        aria-label="Limpar seleção"
        className="rounded-md p-1 text-text-subdued hover:bg-surface hover:text-red-600"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export function PageField(props: FieldProps) {
  const pages = useEditor((s) => s.metadata?.data.pages || []);
  const options = pages.map((p) => ({
    value: String(p.handle || p.id),
    label: p.title,
  }));
  return (
    <DataSelect
      {...props}
      options={options}
      emptyLabel="Selecione uma página"
      emptyHint="Nenhuma página cadastrada."
    />
  );
}

// ---------------------------------------------------------------------------

function toHex(value: any): string {
  if (typeof value !== 'string') return '';
  // `<input type="color">` e `input type="color" value=…` SÓ aceitam
  // #rrggbb (6 dígitos). Valores tipo #fff / #000 que muitas lojas
  // salvaram no settings_data.json caem num warning "does not conform
  // to the required format. The format is #rrggbb". Expandimos aqui
  // pra que o input receba a versão longa e o swatch renderize igual.
  const short3 = value.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (short3) {
    const [, r, g, b] = short3;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  // #rgba (4-digit) → #rrggbbaa — não faz sentido pro type=color, mas
  // expandimos pelo mesmo motivo (input de texto serializa como está
  // e a gente prefere que o value seja sempre normalizado).
  const short4 = value.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (short4) {
    const [, r, g, b, a] = short4;
    return `#${r}${r}${g}${g}${b}${b}${a}${a}`;
  }
  if (/^#[0-9a-f]{6,8}$/i.test(value)) return value;
  // rgb/rgba — capture optional alpha so we can report transparent correctly
  const rgba = value.match(
    /rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i
  );
  if (rgba) {
    const a = rgba[4] !== undefined ? parseFloat(rgba[4]) : 1;
    // Fully transparent → treat as "no color" so the swatch shows a checker
    // pattern instead of pretending it's solid black (#000000).
    if (a === 0) return '';
    return (
      '#' +
      [rgba[1], rgba[2], rgba[3]]
        .map((n) => Number(n).toString(16).padStart(2, '0'))
        .join('')
    );
  }
  return '';
}

/**
 * Build a preview URL for an asset name stored in a theme setting.
 *
 * We use the tenant's themed asset route so the engine's resolveAsset
 * chain tries the full fallback (tenant theme storage → cdn/<theme>/
 * → cdn/default/), same as the `asset_url` Liquid filter uses at
 * render time. Hitting the flat `/api/assets/common/` route here
 * would 404 for theme-scoped assets.
 */
function resolveAsset(
  name: string,
  session: { tenantId: string; themeId: string } | null
): string {
  if (!name) return '';
  if (/^(https?:|data:|\/\/)/.test(name)) return name;
  if (name.startsWith('/')) return name;
  if (!session) return `${ENGINE_BASE}/api/assets/common/${encodeURIComponent(name)}`;
  return `${ENGINE_BASE}/api/assets/${session.tenantId}/${session.themeId}/production/assets/${encodeURIComponent(name)}`;
}
